import { useState, useEffect, useRef } from "react";
import { createPortal } from "react-dom";
import { db } from "../../firebase";
import {
  collection, doc, addDoc, updateDoc, setDoc, getDoc, getDocs, deleteDoc,
  query, where, onSnapshot, serverTimestamp, arrayUnion, arrayRemove, runTransaction,
} from "firebase/firestore";
import { IC, Avatar, Stars, Pill, PlaceAutocomplete, useToast } from "../shared";
import { RouteSelector, RouteMap } from "./TripDetails";
import { downsampleCoords } from "./PostTrip";

// ── helpers ──────────────────────────────────────────────────────────────────
const isToday = (d) => {
  const now = new Date();
  return d.getFullYear() === now.getFullYear() && d.getMonth() === now.getMonth() && d.getDate() === now.getDate();
};
const fmtDate = (d) => d.toLocaleDateString("en", { month: "short", day: "numeric" });
const fmtTime = (d) => d.toLocaleTimeString("en", { hour: "2-digit", minute: "2-digit" });

// ── Trips Screen ──────────────────────────────────────────────────────────────
const TripsScreen = ({ currentUser, forceView, onViewApplied, onNavigateChat, onOpenChat }) => {
  const toast = useToast();
  const [view, setView]               = useState("driving");

  useEffect(() => {
    if (forceView) { setView(forceView); onViewApplied?.(); }
  }, [forceView]); // eslint-disable-line react-hooks/exhaustive-deps
  const [driverTrips, setDriverTrips] = useState([]);
  const [allRequests, setAllRequests] = useState([]);  // requests for my trips
  const [myRequests, setMyRequests]   = useState([]);  // my requests as passenger
  const [riderTrips,  setRiderTrips]  = useState([]);  // full trip docs for trips I'm riding in
  const [ratedSet, setRatedSet]       = useState(new Set());
  const [ratedScores, setRatedScores] = useState({});  // rId -> score
  const [confirmAction, setConfirmAction] = useState(null); // { message, onConfirm }
  const [confirmBusy, setConfirmBusy]     = useState(false);
  const [rejectModal, setRejectModal]   = useState(null);  // { req }
  const [editRouteId, setEditRouteId]       = useState(null);
  const [editWaypoints, setEditWaypoints]   = useState([]);
  const [selectedEditRoute, setSelectedEditRoute] = useState(null);
  const [savingRoute, setSavingRoute]       = useState(false);
  const [addingStop, setAddingStop]         = useState(false);
  const [stopLabel, setStopLabel]           = useState("");
  const [acceptingId, setAcceptingId]       = useState(null);
  const stopLabelRef = useRef("");
  const [, tick] = useState(0);
  const [ratedComments, setRatedComments]   = useState({});  // rId -> comment text
  const [commentModal, setCommentModal]     = useState(null); // { rId, ratedId, tripId, ratedName }
  const [commentText, setCommentText]       = useState("");

  useEffect(() => {
    const q1 = query(collection(db, "trips"), where("driverId", "==", currentUser.id));
    const unsub1 = onSnapshot(q1, snap => setDriverTrips(snap.docs.map(d => ({ id: d.id, ...d.data() }))));
    const q2 = query(collection(db, "requests"), where("driverId", "==", currentUser.id));
    const unsub2 = onSnapshot(q2, snap => setAllRequests(snap.docs.map(d => ({ id: d.id, ...d.data() }))));
    const q3 = query(collection(db, "requests"), where("passengerId", "==", currentUser.id));
    const unsub3 = onSnapshot(q3, snap => setMyRequests(snap.docs.map(d => ({ id: d.id, ...d.data() }))));
    const q4 = query(collection(db, "trips"), where("passengers", "array-contains", currentUser.id));
    const unsub4 = onSnapshot(q4, snap => setRiderTrips(snap.docs.map(d => ({ id: d.id, ...d.data() }))));
    return () => { unsub1(); unsub2(); unsub3(); unsub4(); };
  }, [currentUser.id]);

  // Load existing ratings so the widget shows "Rated X/5" for already-rated trips
  useEffect(() => {
    const q = query(collection(db, "ratings"), where("raterId", "==", currentUser.id));
    getDocs(q).then(snap => {
      const ids = new Set();
      const scores = {};
      const comments = {};
      snap.docs.forEach(d => { ids.add(d.id); scores[d.id] = d.data().score; if (d.data().comment) comments[d.id] = d.data().comment; });
      setRatedSet(ids);
      setRatedScores(scores);
      setRatedComments(comments);
    });
  }, [currentUser.id]);

  const accept = async req => {
    if (acceptingId) return;
    setAcceptingId(req.id);
    try {
      const tripRef = doc(db, "trips", req.tripId);
      const accepted = await runTransaction(db, async (txn) => {
        const tripSnap = await txn.get(tripRef);
        if (!tripSnap.exists()) throw new Error("Trip not found");
        const t = tripSnap.data();
        const passengers = t.passengers || [];
        const cleanPassengers = passengers.filter(id => id !== req.passengerId);
        if (cleanPassengers.length >= t.seats) throw new Error("No seats left");
        const newPassengers = [...cleanPassengers, req.passengerId];
        txn.update(tripRef, {
          passengers: newPassengers,
          status: newPassengers.length >= t.seats ? "full" : "active",
        });
        txn.update(doc(db, "requests", req.id), { status: "accepted" });
        return true;
      });
      if (!accepted) return;
      await addDoc(collection(db, "notifications", req.passengerId, "items"), {
        text: `Your seat was confirmed! You're going to ${req.tripTo}.`, tab: "trips", subview: "riding", type: "accepted", ts: serverTimestamp(), read: false,
      });
      toast(`${req.passengerName}'s seat confirmed!`, "success");
    } catch (e) {
      console.error("accept error:", e);
      const msg = e.message === "No seats left" ? "No seats left on this trip."
        : e.message === "Trip not found" ? "This trip no longer exists."
        : "Failed to accept request. Please try again.";
      toast(msg, "error");
    } finally {
      setAcceptingId(null);
    }
  };

  const deleteChat = async (requestId) => {
    const msgSnap = await getDocs(collection(db, "chats", requestId, "messages"));
    const batch = msgSnap.docs.map(d => deleteDoc(doc(db, "chats", requestId, "messages", d.id)));
    await Promise.all(batch);
  };

  const REJECT_REASONS = [
    "I won't take that route",
    "Too far out of my way",
    "No more seats available",
    "I prefer a direct trip",
    "Other",
  ];

  const reject = (req) => {
    if (req.isPitStop) {
      setRejectModal({ req });
    } else {
      doReject(req, null);
    }
  };

  const doReject = async (req, reason) => {
    await updateDoc(doc(db, "requests", req.id), { status: "rejected", rejectReason: reason || null });
    deleteChat(req.id).catch(() => {});
    await addDoc(collection(db, "notifications", req.passengerId, "items"), {
      text: reason
        ? `Your ride request (${req.pickupFrom} → ${req.dropoffTo}) was declined: "${reason}".`
        : `Your request for the trip to ${req.tripTo} was declined.`,
      tab: "trips", subview: "riding", type: "rejected", ts: serverTimestamp(), read: false,
    });
    toast("Request declined.", "info");
  };

  const cancelTrip = (trip) => {
    setConfirmAction({
      type: "trip",
      message: `Cancel your trip to ${trip.to}? All passengers will be notified.`,
      onConfirm: async () => {
        await updateDoc(doc(db, "trips", trip.id), { status: "cancelled", passengers: [] });
        const reqs = allRequests.filter(r => r.tripId === trip.id && (r.status === "pending" || r.status === "accepted"));
        for (const req of reqs) {
          await updateDoc(doc(db, "requests", req.id), { status: "cancelled" });
          deleteChat(req.id).catch(() => {});
          await addDoc(collection(db, "notifications", req.passengerId, "items"), {
            text: `The trip to ${trip.to} was cancelled by the driver.`, tab: "trips", subview: "riding", type: "cancelled", ts: serverTimestamp(), read: false,
          });
        }
        toast("Trip cancelled.", "info");
      },
    });
  };

  const getRefundPolicy = (tripDatetime) => {
    const hoursUntil = (new Date(tripDatetime) - new Date()) / 3600000;
    if (hoursUntil >= 24) return "Full refund (24h+ before trip)";
    if (hoursUntil >= 2)  return "50% refund (2–24h before trip)";
    return "No refund (under 2h before trip)";
  };

  const cancelSeat = (req) => {
    const refundNote = req.status === "accepted" ? `\n\n${getRefundPolicy(req.tripDatetime)}` : "";
    setConfirmAction({
      message: `Cancel your seat on the trip to ${req.tripTo}?${refundNote}`,
      onConfirm: async () => {
        const wasAccepted = req.status === "accepted";
        await updateDoc(doc(db, "requests", req.id), { status: "cancelled" });
        if (wasAccepted) {
          await updateDoc(doc(db, "trips", req.tripId), { passengers: arrayRemove(req.passengerId), status: "active" });
        }
        deleteChat(req.id).catch(() => {});
        addDoc(collection(db, "notifications", req.driverId, "items"), {
          text: wasAccepted
            ? `${req.passengerName} cancelled their confirmed seat on your trip to ${req.tripTo}.`
            : `${req.passengerName} cancelled their seat request on your trip to ${req.tripTo}.`,
          tab: "trips", subview: "driving",
          type: wasAccepted ? "cancelled" : "request_withdrawn",
          ts: serverTimestamp(), read: false,
        }).catch(() => {});
        toast("Your seat has been cancelled.", "info");
      },
    });
  };

  const saveRoute = async (trip) => {
    setSavingRoute(true);
    const wps = editWaypoints.filter(w => w.lat && w.lon).map(({ label, lat, lon }) => ({ label, lat, lon }));
    const newGeometry = downsampleCoords(selectedEditRoute?.coords || trip.routeGeometry || null);
    await updateDoc(doc(db, "trips", trip.id), { waypoints: wps, routeGeometry: newGeometry });

    const haversine = (lat1, lon1, lat2, lon2) => {
      const R = 6371, toRad = x => x * Math.PI / 180;
      const dLat = toRad(lat2 - lat1), dLon = toRad(lon2 - lon1);
      const a = Math.sin(dLat/2)**2 + Math.cos(toRad(lat1))*Math.cos(toRad(lat2))*Math.sin(dLon/2)**2;
      return R * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1-a));
    };
    const isNearRoute = (lat, lon, coords) => {
      if (!coords?.length) return false;
      return coords.some(c => {
        const p = Array.isArray(c) ? { lat: c[0], lng: c[1] } : c;
        return haversine(lat, lon, p.lat, p.lng ?? p.lon) < 3;
      });
    };

    const accepted = allRequests.filter(r => r.tripId === trip.id && r.status === "accepted");
    for (const req of accepted) {
      if (req.isPitStop && req.pickupFromLat && req.dropoffToLat && newGeometry?.length) {
        const pickupOk  = isNearRoute(req.pickupFromLat, req.pickupFromLon, newGeometry);
        const dropoffOk = isNearRoute(req.dropoffToLat,  req.dropoffToLon,  newGeometry);
        if (!pickupOk || !dropoffOk) {
          await updateDoc(doc(db, "requests", req.id), { status: "cancelled" });
          await updateDoc(doc(db, "trips", trip.id), { passengers: arrayRemove(req.passengerId) });
          await addDoc(collection(db, "notifications", req.passengerId, "items"), {
            text: `Your seat (${req.pickupFrom} → ${req.dropoffTo}) was cancelled — it's no longer along the driver's new route.`,
            tab: "trips", subview: "riding", type: "cancelled", ts: serverTimestamp(), read: false,
          });
        } else {
          await addDoc(collection(db, "notifications", req.passengerId, "items"), {
            text: `The driver updated the route for your trip to ${trip.to}.`, tab: "trips", subview: "riding", type: "route_update", ts: serverTimestamp(), read: false,
          });
        }
      } else {
        await addDoc(collection(db, "notifications", req.passengerId, "items"), {
          text: `The driver updated the route for your trip to ${trip.to}.`, tab: "trips", subview: "riding", type: "route_update", ts: serverTimestamp(), read: false,
        });
      }
    }
    setEditRouteId(null); setEditWaypoints([]); setSelectedEditRoute(null); setAddingStop(false); setSavingRoute(false);
  };

  const rateUser = async (ratedId, tripId, score, requestId, ratingField, ratedAs) => {
    const rId = `${currentUser.id}_${ratedId}_${tripId}`;
    if (ratedSet.has(rId)) return;
    // Mark as rated immediately in local state
    setRatedSet(prev => new Set([...prev, rId]));
    setRatedScores(prev => ({ ...prev, [rId]: score }));
    tick(x => x + 1);
    try {
      // Save to ratings collection — include ratedAs ("driver" or "passenger")
      await setDoc(doc(db, "ratings", rId), { raterId: currentUser.id, ratedId, tripId, score, ratedAs: ratedAs || null, ratedAt: serverTimestamp() });
      // Save to request document
      if (requestId && ratingField) {
        await updateDoc(doc(db, "requests", requestId), { [ratingField]: score, [`${ratingField}At`]: serverTimestamp() });
      }
      // Update rated user's average rating
      const userRef = doc(db, "users", ratedId);
      await runTransaction(db, async (txn) => {
        const snap = await txn.get(userRef);
        if (!snap.exists()) return;
        const u = snap.data();
        const totalRatings = (u.ratingCount || 0) + 1;
        const averageRating = (((u.rating || 0) * (u.ratingCount || 0)) + score) / totalRatings;
        txn.update(userRef, { rating: averageRating, ratingCount: totalRatings });
      });
    } catch (e) {
      console.error("Rating save failed:", e.code, e.message, e);
      // Revert local state on failure
      setRatedSet(prev => { const next = new Set(prev); next.delete(rId); return next; });
      setRatedScores(prev => { const next = { ...prev }; delete next[rId]; return next; });
      toast("Failed to save rating. Please try again.", "error");
    }
  };

  // ── Save comment to an existing rating ──
  const saveComment = async () => {
    if (!commentModal || !commentText.trim()) return;
    const { rId, ratedId, tripId } = commentModal;
    const text = commentText.trim();
    try {
      await updateDoc(doc(db, "ratings", rId), { comment: text });
      setRatedComments(prev => ({ ...prev, [rId]: text }));
      toast("Review saved!", "success");
    } catch (e) {
      console.error("Save comment failed:", e);
      toast("Failed to save review. Please try again.", "error");
    }
    setCommentModal(null);
    setCommentText("");
  };

  // ── Rating widget ──
  const RatingWidget = ({ ratedId, tripId, label, requestId, ratingField, ratedName, ratedAs }) => {
    const rId = `${currentUser.id}_${ratedId}_${tripId}`;
    const done = ratedSet.has(rId);
    const score = ratedScores[rId];
    const comment = ratedComments[rId];
    const [hover, setHover] = useState(0);
    if (done) return (
      <div>
        <div style={{ fontFamily: "Syne", fontWeight: 700, fontSize: 13, color: "var(--warning)", marginBottom: comment ? 4 : 0 }}>★ Rated {score || "?"}/5</div>
        {comment ? (
          <div style={{ fontSize: 12, color: "var(--muted)", fontStyle: "italic", lineHeight: 1.4 }}>"{comment}"</div>
        ) : (
          <button onClick={() => { setCommentModal({ rId, ratedId, tripId, ratedName: ratedName || "this user" }); setCommentText(""); }}
            style={{ display: "flex", alignItems: "center", gap: 6, marginTop: 6, padding: "7px 14px", borderRadius: 10, fontFamily: "Syne", fontWeight: 700, fontSize: 12, background: "rgba(43,91,224,.08)", color: "var(--accent)", border: "1px solid rgba(43,91,224,.15)", cursor: "pointer", transition: "all .15s" }}>
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M21 15a2 2 0 01-2 2H7l-4 4V5a2 2 0 012-2h14a2 2 0 012 2z"/></svg>
            Add Review
          </button>
        )}
      </div>
    );
    return (
      <div>
        {label && <div style={{ fontSize: 11, fontFamily: "Syne", fontWeight: 700, color: "var(--warning)", textTransform: "uppercase", letterSpacing: "0.06em", marginBottom: 8 }}>{label}</div>}
        <div style={{ display: "flex", alignItems: "center", gap: 2 }}>
          {[1,2,3,4,5].map(n => (
            <button key={n} onMouseEnter={() => setHover(n)} onMouseLeave={() => setHover(0)} onClick={() => rateUser(ratedId, tripId, n, requestId, ratingField, ratedAs)} style={{ width: 28, height: 28, color: n <= hover ? "#f5a623" : "#ddd", transition: "color .1s", background: "none", border: "none", cursor: "pointer", padding: 0 }}>{IC.star}</button>
          ))}
        </div>
      </div>
    );
  };

  // ── Status pill helpers ──
  const driverPill = (trip) => {
    const dt = new Date(trip.datetime);
    const isPast = dt < new Date();
    if (isPast && trip.status !== "cancelled") return { label: "COMPLETED", bg: "var(--warm)", color: "var(--muted)", border: "none" };
    if (trip.status === "full") return { label: "FULL", bg: "rgba(43,91,224,.08)", color: "var(--accent)", border: "none" };
    if (trip.status === "cancelled") return { label: "CANCELLED", bg: "var(--danger-soft)", color: "var(--danger)", border: "1px solid var(--danger)" };
    if (isToday(dt)) return { label: "ACTIVE", bg: "rgba(15,158,104,.08)", color: "var(--success)", border: "1px solid var(--success)" };
    return { label: "UPCOMING", bg: "rgba(245,166,35,.08)", color: "var(--warning)", border: "1px solid var(--warning)" };
  };

  const passengerPill = (req) => {
    const dt = new Date(req.tripDatetime);
    const isPast = dt < new Date();
    if (req.status === "cancelled") return { label: "CANCELLED", bg: "var(--danger-soft)", color: "var(--danger)", border: "1px solid var(--danger)" };
    if (isPast) return { label: "COMPLETED", bg: "var(--warm)", color: "var(--muted)", border: "none" };
    if (req.status === "accepted") return { label: "CONFIRMED", bg: "rgba(15,158,104,.08)", color: "var(--success)", border: "1px solid var(--success)" };
    return { label: "PENDING", bg: "rgba(43,91,224,.08)", color: "var(--accent)", border: "1px solid rgba(43,91,224,0.3)" };
  };

  const StatusPill = ({ pill }) => (
    <span style={{ display: "inline-block", padding: "4px 12px", borderRadius: 99, fontFamily: "Syne", fontWeight: 700, fontSize: 11, letterSpacing: "0.04em", background: pill.bg, color: pill.color, border: pill.border || "none" }}>{pill.label}</span>
  );

  // ── Route dot ──
  const Dot = ({ color = "var(--ink)" }) => <span style={{ width: 7, height: 7, borderRadius: "50%", background: color, display: "inline-block", flexShrink: 0 }}/>;

  return (
    <div className="screen" style={{ padding: "20px 16px 90px" }}>
      <h2 style={{ fontFamily: "Syne", fontWeight: 800, fontSize: 24, color: "var(--ink)", marginBottom: 16 }}>My Trips</h2>

      {/* ── Tab toggle ── */}
      <div style={{ display: "flex", background: "var(--warm)", borderRadius: 14, padding: 4, marginBottom: 20, gap: 4 }}>
        {[["driving","As Driver"],["riding","As Passenger"]].map(([v, l]) => (
          <button key={v} onClick={() => setView(v)} style={{ flex: 1, padding: 10, textAlign: "center", borderRadius: 10, fontFamily: "Syne", fontSize: 13, fontWeight: 700, color: view === v ? "var(--ink)" : "var(--muted)", background: view === v ? "var(--card)" : "transparent", boxShadow: view === v ? "var(--shadow)" : "none", border: "none", cursor: "pointer", transition: "all .2s" }}>{l}</button>
        ))}
      </div>

      {/* ══════════════════════════════════════════════════════════════════════ */}
      {/* ── AS DRIVER ── */}
      {/* ══════════════════════════════════════════════════════════════════════ */}
      {view === "driving" && (() => {
        const now = new Date();
        const active = driverTrips.filter(t => t.status !== "cancelled" && isToday(new Date(t.datetime)) && new Date(t.datetime) > now);
        const upcoming = driverTrips.filter(t => t.status !== "cancelled" && !isToday(new Date(t.datetime)) && new Date(t.datetime) > now)
          .sort((a, b) => new Date(a.datetime) - new Date(b.datetime));
        const completed = driverTrips.filter(t => t.status !== "cancelled" && new Date(t.datetime) <= now)
          .sort((a, b) => new Date(b.datetime) - new Date(a.datetime));
        const cancelled = driverTrips.filter(t => t.status === "cancelled")
          .sort((a, b) => new Date(b.datetime) - new Date(a.datetime));
        const allTrips = [...active, ...upcoming, ...completed, ...cancelled];
        return allTrips.length === 0
          ? <div style={{ textAlign: "center", padding: "50px 0", color: "var(--muted)" }}><div style={{ fontSize: 48 }}>🚗</div><div style={{ fontFamily: "Syne", fontWeight: 700, fontSize: 16, marginTop: 12 }}>No active trips</div><div style={{ fontSize: 13, marginTop: 4 }}>Post a trip to get started</div></div>
          : allTrips.map((trip, tidx) => {
              const pending  = allRequests.filter(r => r.tripId === trip.id && r.status === "pending");
              const accepted = allRequests.filter(r => r.tripId === trip.id && r.status === "accepted");
              const dt = new Date(trip.datetime);
              const isPast = dt < new Date();
              const pill = driverPill(trip);
              const isUpcoming = pill.label === "UPCOMING";
              const isActive = pill.label === "ACTIVE";
              const seatsLeft = trip.seats - (trip.passengers?.length || 0);
              return (
                <div key={trip.id} className="acard" style={{ animationDelay: `${tidx * 50}ms`, background: "var(--card)", borderRadius: 20, padding: 16, border: "1.5px solid var(--border)", marginBottom: 12, boxShadow: "var(--shadow)" }}>

                  {/* ── Top row: pill + date ── */}
                  <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 12 }}>
                    <StatusPill pill={pill}/>
                    <span style={{ fontSize: 13, color: "var(--muted)", fontWeight: 500 }}>{fmtDate(dt)} · {fmtTime(dt)}</span>
                  </div>

                  {/* ── Route ── */}
                  <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 4 }}>
                    <Dot/>
                    <span style={{ fontFamily: "Syne", fontWeight: 700, fontSize: 15, color: "var(--ink)" }}>{trip.from} → {trip.to}</span>
                  </div>

                  {/* ── Seats + price row ── */}
                  <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 12 }}>
                    <div style={{ display: "flex", alignItems: "center", gap: 8, color: "var(--muted)", fontSize: 12 }}>
                      {trip.distance && <><Dot color="var(--accent)"/><span>{trip.distance}</span></>}
                      <span style={{ width: 14, height: 14, display: "inline-flex" }}>{IC.seat}</span>
                      <span style={{ fontFamily: "Syne", fontWeight: 700, fontSize: 12, color: "var(--success)", textTransform: "uppercase" }}>{seatsLeft} {seatsLeft === 1 ? "seat" : "seats"} left</span>
                    </div>
                    <span style={{ fontFamily: "Syne", fontWeight: 800, fontSize: 20, color: "var(--accent)" }}>€{Number(trip.price).toFixed(2)}</span>
                  </div>

                  {/* ── Edit route ── */}
                  {trip.fromLat && trip.status !== "cancelled" && (
                    <div style={{ marginBottom: 10 }}>
                      {editRouteId !== trip.id ? (
                        !isPast && <button onClick={() => { setEditRouteId(trip.id); setEditWaypoints(trip.waypoints || []); setSelectedEditRoute(null); setAddingStop(false); }} style={{ fontSize: 12, color: "var(--accent)", fontFamily: "Syne", fontWeight: 700, background: "var(--warm)", border: "none", borderRadius: 8, padding: "6px 12px", cursor: "pointer" }}>
                          {IC.pin && <span style={{ width: 12, height: 12, display: "inline-block", verticalAlign: "middle", marginRight: 4 }}>{IC.pin}</span>}Edit Route
                        </button>
                      ) : (
                        <div>
                          <RouteSelector
                            fromCoords={{ lat: trip.fromLat, lon: trip.fromLon }}
                            toCoords={{ lat: trip.toLat, lon: trip.toLon }}
                            waypoints={editWaypoints}
                            onChange={setSelectedEditRoute}
                          />
                          <div style={{ marginTop: 10 }}>
                            <div style={{ fontSize: 11, fontFamily: "Syne", fontWeight: 700, color: "var(--muted)", textTransform: "uppercase", letterSpacing: "0.06em", marginBottom: 8 }}>Via stops</div>
                            {editWaypoints.map((wp, i) => (
                              <div key={wp.label + i} style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 6 }}>
                                <span style={{ width: 8, height: 8, borderRadius: "50%", background: "#9b59b6", flexShrink: 0 }}/>
                                <span style={{ flex: 1, fontSize: 13, color: "var(--ink)" }}>{wp.label}</span>
                                <button onClick={() => setEditWaypoints(wps => wps.filter((_, j) => j !== i))} style={{ fontSize: 18, color: "var(--muted)", lineHeight: 1, padding: "2px 6px", background: "none", border: "none" }}>×</button>
                              </div>
                            ))}
                            {addingStop ? (
                              <PlaceAutocomplete
                                placeholder="Search for a stop..."
                                value={stopLabel}
                                onChange={v => { setStopLabel(v); stopLabelRef.current = v; }}
                                onCoords={coords => {
                                  setEditWaypoints(wps => [...wps, { label: stopLabelRef.current, lat: coords.lat, lon: coords.lon }]);
                                  setStopLabel(""); stopLabelRef.current = ""; setAddingStop(false);
                                }}
                              />
                            ) : (
                              <button onClick={() => setAddingStop(true)} style={{ fontSize: 12, color: "var(--accent)", fontFamily: "Syne", fontWeight: 700, padding: "4px 0", background: "none", border: "none" }}>+ Add stop</button>
                            )}
                            <div style={{ display: "flex", gap: 8, marginTop: 12 }}>
                              <button onClick={() => { setEditRouteId(null); setEditWaypoints([]); setAddingStop(false); }} style={{ flex: 1, padding: "10px", borderRadius: 12, fontFamily: "Syne", fontWeight: 700, fontSize: 13, background: "var(--warm)", color: "var(--muted)", border: "none", cursor: "pointer" }}>Cancel</button>
                              <button onClick={() => saveRoute(trip)} disabled={savingRoute} style={{ flex: 1, padding: "10px", borderRadius: 12, fontFamily: "Syne", fontWeight: 700, fontSize: 13, background: "var(--accent)", color: "#fff", border: "none", cursor: "pointer" }}>{savingRoute ? "Saving…" : "Save Route"}</button>
                            </div>
                          </div>
                        </div>
                      )}
                    </div>
                  )}

                  {/* ── Pending requests (active/upcoming trips only) ── */}
                  {trip.status !== "cancelled" && (isActive || isUpcoming) && pending.length > 0 && (
                    <div style={{ borderTop: "1px solid var(--border)", paddingTop: 12 }}>
                      <div style={{ fontSize: 11, fontFamily: "Syne", fontWeight: 700, color: "var(--warning)", textTransform: "uppercase", letterSpacing: "0.06em", marginBottom: 10 }}>Pending Request{pending.length > 1 ? "s" : ""}</div>
                      {pending.map(req => {
                        const pass = { id: req.passengerId, name: req.passengerName, avatar: req.passengerAvatar, rating: req.passengerRating || 0 };
                        return (
                          <div key={req.id} style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 10 }}>
                            <Avatar user={pass} size={42}/>
                            <div style={{ flex: 1, minWidth: 0 }}>
                              <div style={{ fontFamily: "Syne", fontWeight: 700, fontSize: 14, color: "var(--ink)" }}>{pass.name}</div>
                              <div style={{ display: "flex", alignItems: "center", gap: 4 }}>
                                <Stars rating={pass.rating} size={11}/>
                              </div>
                              {req.isPitStop && <div style={{ fontSize: 11, color: "var(--accent)", fontWeight: 600, marginTop: 2 }}>Pickup: {req.pickupFrom} → {req.dropoffTo} · €{req.segmentPrice}</div>}
                            </div>
                            <div style={{ display: "flex", gap: 8, flexShrink: 0 }}>
                              <button onClick={() => accept(req)} disabled={acceptingId === req.id} style={{ padding: "9px 18px", borderRadius: 10, fontFamily: "Syne", fontWeight: 700, fontSize: 13, background: "var(--success)", color: "#fff", border: "none", cursor: acceptingId === req.id ? "wait" : "pointer", opacity: acceptingId === req.id ? 0.6 : 1, transition: "opacity .15s" }}>
                                {acceptingId === req.id
                                  ? <span style={{ width: 14, height: 14, border: "2px solid #fff", borderTopColor: "transparent", borderRadius: "50%", animation: "igo-spin .7s linear infinite", display: "inline-block" }}/>
                                  : "Accept"}
                              </button>
                              <button onClick={() => reject(req)} style={{ padding: "9px 18px", borderRadius: 10, fontFamily: "Syne", fontWeight: 700, fontSize: 13, background: "var(--danger-soft)", color: "var(--danger)", border: "1px solid rgba(192,57,43,0.3)", cursor: "pointer" }}>
                                Reject
                              </button>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  )}

                  {/* ── Accepted passengers (active/upcoming) ── */}
                  {trip.status !== "cancelled" && !isPast && accepted.length > 0 && (
                    <div style={{ borderTop: "1px solid var(--border)", paddingTop: 12, marginTop: 4 }}>
                      <div style={{ fontSize: 11, fontFamily: "Syne", fontWeight: 700, color: "var(--success)", textTransform: "uppercase", letterSpacing: "0.06em", marginBottom: 10 }}>Accepted</div>
                      {accepted.map(req => {
                        const pass = { id: req.passengerId, name: req.passengerName, avatar: req.passengerAvatar, rating: req.passengerRating || 0 };
                        return (
                          <div key={req.id} style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 8 }}>
                            <Avatar user={pass} size={36}/>
                            <div style={{ flex: 1 }}>
                              <div style={{ fontFamily: "Syne", fontWeight: 600, fontSize: 14, color: "var(--ink)" }}>{pass.name}</div>
                            </div>
                            <button onClick={() => onOpenChat?.(req)} style={{ display: "flex", alignItems: "center", gap: 6, padding: "7px 14px", borderRadius: 10, fontFamily: "Syne", fontWeight: 700, fontSize: 12, background: "rgba(43,91,224,.08)", color: "var(--accent)", border: "1px solid rgba(43,91,224,.15)", cursor: "pointer", transition: "all .15s" }}>
                              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M21 15a2 2 0 01-2 2H7l-4 4V5a2 2 0 012-2h14a2 2 0 012 2z"/></svg>
                              Message
                            </button>
                          </div>
                        );
                      })}
                    </div>
                  )}

                  {/* ── Rate passengers (completed only — never cancelled) ── */}
                  {trip.status !== "cancelled" && isPast && accepted.length > 0 && (
                    <div style={{ borderTop: "1px solid var(--border)", paddingTop: 12, marginTop: 4 }}>
                      {accepted.map(req => {
                        const pass = { id: req.passengerId, name: req.passengerName, avatar: req.passengerAvatar };
                        return (
                          <div key={req.id} style={{ marginBottom: 8 }}>
                            <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 8 }}>
                              <Avatar user={pass} size={30}/>
                              <div style={{ fontWeight: 600, fontSize: 13, flex: 1, color: "var(--ink)" }}>{pass.name}</div>
                            </div>
                            <RatingWidget ratedId={pass.id} tripId={trip.id} label="Rate Passenger" requestId={req.id} ratingField="driverRating" ratedName={pass.name} ratedAs="passenger"/>
                          </div>
                        );
                      })}
                    </div>
                  )}

                  {/* ── Cancel Trip button (upcoming only) ── */}
                  {isUpcoming && trip.status !== "cancelled" && (
                    <button onClick={() => cancelTrip(trip)} style={{ width: "100%", padding: 12, borderRadius: 12, fontFamily: "Syne", fontWeight: 700, fontSize: 13, background: "var(--danger-soft)", color: "var(--danger)", border: "1px solid var(--danger)", cursor: "pointer", marginTop: 8, transition: "opacity .15s" }}>
                      Cancel Trip
                    </button>
                  )}
                </div>
              );
            })
        ;
      })()}

      {/* ══════════════════════════════════════════════════════════════════════ */}
      {/* ── AS PASSENGER ── */}
      {/* ══════════════════════════════════════════════════════════════════════ */}
      {view === "riding" && (() => {
        const now = new Date();
        const futureReqs = myRequests.filter(r => new Date(r.tripDatetime) > now);
        const bestByTrip = new Map();
        const priority = { accepted: 0, pending: 1 };
        for (const r of futureReqs) {
          if (r.status === "cancelled" || r.status === "rejected") continue;
          const prev = bestByTrip.get(r.tripId);
          if (!prev || (priority[r.status] ?? 2) < (priority[prev.status] ?? 2)) bestByTrip.set(r.tripId, r);
        }
        // Split future rides into confirmed and pending, sort by date ascending
        const futureRides = [...bestByTrip.values()];
        const confirmed = futureRides.filter(r => r.status === "accepted")
          .sort((a, b) => new Date(a.tripDatetime) - new Date(b.tripDatetime));
        const pending = futureRides.filter(r => r.status === "pending")
          .sort((a, b) => new Date(a.tripDatetime) - new Date(b.tripDatetime));

        // Completed: past accepted, sorted by date descending
        const completedRides = myRequests.filter(r => new Date(r.tripDatetime) <= now && r.status === "accepted")
          .sort((a, b) => new Date(b.tripDatetime) - new Date(a.tripDatetime));
        // Cancelled: only show passenger-cancelled or driver-cancelled-trip, never rejected
        const cancelledRides = myRequests.filter(r => r.status === "cancelled" && r.status !== "rejected")
          .sort((a, b) => new Date(b.tripDatetime) - new Date(a.tripDatetime));
        const allRides = [...confirmed, ...pending, ...completedRides, ...cancelledRides];

        return allRides.length === 0
          ? <div style={{ textAlign: "center", padding: "50px 0", color: "var(--muted)" }}><div style={{ fontSize: 48 }}>🧳</div><div style={{ fontFamily: "Syne", fontWeight: 700, fontSize: 16, marginTop: 12 }}>No active rides</div><div style={{ fontSize: 13, marginTop: 4 }}>Find a trip to get started</div></div>
          : allRides.map((req, ridx) => {
              const dt = new Date(req.tripDatetime);
              const isPast = dt < new Date();
              const driver = { id: req.driverId, name: req.driverName, avatar: req.driverAvatar, rating: req.driverRating || 0 };
              const riderTrip = riderTrips.find(t => t.id === req.tripId);
              const pFromCoords = req.isPitStop && req.pickupFromLat ? { lat: req.pickupFromLat, lon: req.pickupFromLon } : null;
              const pToCoords   = req.isPitStop && req.dropoffToLat  ? { lat: req.dropoffToLat,  lon: req.dropoffToLon  } : null;
              const pill = passengerPill(req);
              const isConfirmed = pill.label === "CONFIRMED";
              return (
                <div key={req.id} className="acard" style={{ animationDelay: `${ridx * 50}ms`, background: "var(--card)", borderRadius: 20, padding: 16, border: "1.5px solid var(--border)", marginBottom: 12, boxShadow: "var(--shadow)" }}>

                  {/* ── Top row: pill + date ── */}
                  <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 12 }}>
                    <StatusPill pill={pill}/>
                    <span style={{ fontSize: 13, color: "var(--muted)", fontWeight: 500 }}>{fmtDate(dt)} · {fmtTime(dt)}</span>
                  </div>

                  {/* ── Route ── */}
                  <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 4 }}>
                    <Dot/>
                    <span style={{ fontFamily: "Syne", fontWeight: 700, fontSize: 15, color: "var(--ink)" }}>{req.tripFrom} → {req.tripTo}</span>
                  </div>
                  {req.isPitStop && <div style={{ fontSize: 12, color: "#9b59b6", fontWeight: 600, marginLeft: 15, marginBottom: 4 }}>Pickup: {req.pickupFrom} → {req.dropoffTo}</div>}

                  {/* ── Distance + price ── */}
                  <div style={{ display: "flex", alignItems: "center", gap: 8, color: "var(--muted)", fontSize: 12, marginBottom: 12, marginLeft: 15 }}>
                    {riderTrip?.distance && <><Dot color="var(--accent)"/><span>{riderTrip.distance}</span><span>·</span></>}
                    <span>€{req.isPitStop ? req.segmentPrice : req.tripPrice}</span>
                  </div>

                  {/* ── Driver cancelled notice ── */}
                  {req.status === "cancelled" && riderTrip?.status === "cancelled" && (
                    <div style={{ fontSize: 12, fontFamily: "Syne", fontWeight: 600, color: "var(--danger)", marginBottom: 8 }}>Driver cancelled this trip</div>
                  )}

                  {/* ── Driver row ── */}
                  <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: riderTrip ? 10 : 0 }}>
                    <Avatar user={driver} size={36}/>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ fontWeight: 600, fontSize: 14, color: "var(--ink)" }}>{driver.name}</div>
                      <Stars rating={driver.rating} size={11}/>
                    </div>
                    {isConfirmed && !isPast && (
                      <button onClick={() => cancelSeat(req)} style={{ padding: "7px 14px", borderRadius: 10, fontFamily: "Syne", fontWeight: 700, fontSize: 12, background: "none", color: "var(--danger)", border: "none", cursor: "pointer" }}>Cancel</button>
                    )}
                  </div>

                  {/* ── Route map ── */}
                  {riderTrip && (
                    <div style={{ marginTop: 8, marginBottom: isPast && req.status === "accepted" ? 10 : 0 }}>
                      <RouteMap trip={riderTrip} waypoints={riderTrip.waypoints} passengerFromCoords={pFromCoords} passengerToCoords={pToCoords}/>
                    </div>
                  )}

                  {/* ── Message button (confirmed + not past) ── */}
                  {isConfirmed && !isPast && (
                    <button onClick={() => onOpenChat?.(req)} style={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 6, width: "100%", padding: 10, borderRadius: 12, fontFamily: "Syne", fontWeight: 700, fontSize: 12, background: "rgba(43,91,224,.08)", color: "var(--accent)", border: "1px solid rgba(43,91,224,.15)", cursor: "pointer", marginTop: 8 }}>
                      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M21 15a2 2 0 01-2 2H7l-4 4V5a2 2 0 012-2h14a2 2 0 012 2z"/></svg>
                      Message Driver
                    </button>
                  )}

                  {/* ── Rate driver (completed only — never cancelled/rejected) ── */}
                  {isPast && req.status === "accepted" && req.status !== "cancelled" && (
                    <div style={{ borderTop: "1px solid var(--border)", paddingTop: 12, marginTop: 8 }}>
                      <RatingWidget ratedId={driver.id} tripId={req.tripId} label="Rate This Trip" requestId={req.id} ratingField="passengerRating" ratedName={driver.name} ratedAs="driver"/>
                    </div>
                  )}
                </div>
              );
            })
        ;
      })()}

      {/* ── Reject modal ── */}
      {rejectModal && createPortal(
        <div style={{ position: "fixed", top: 0, left: 0, right: 0, bottom: 0, background: "rgba(0,0,0,0.5)", display: "flex", alignItems: "flex-end", zIndex: 1000, paddingBottom: "calc(80px + env(safe-area-inset-bottom, 0px))" }}>
          <div className="aslide" style={{ background: "var(--card)", borderRadius: 24, padding: "24px 20px", margin: "0 16px", width: "100%" }}>
            <div style={{ fontFamily: "Syne", fontWeight: 700, fontSize: 17, color: "var(--ink)", marginBottom: 4 }}>Decline request</div>
            <div style={{ fontSize: 13, color: "var(--muted)", marginBottom: 16 }}>{rejectModal.req.passengerName} · {rejectModal.req.pickupFrom} → {rejectModal.req.dropoffTo}</div>
            <div style={{ display: "flex", flexDirection: "column", gap: 8, marginBottom: 16 }}>
              {REJECT_REASONS.map(reason => (
                <button key={reason} onClick={async () => { await doReject(rejectModal.req, reason); setRejectModal(null); }} style={{ padding: "12px 16px", borderRadius: 12, border: "1.5px solid var(--border)", background: "var(--warm)", fontFamily: "Syne", fontWeight: 600, fontSize: 13, color: "var(--ink)", textAlign: "left", cursor: "pointer", transition: "background .15s" }}>
                  {reason}
                </button>
              ))}
            </div>
            <button onClick={() => setRejectModal(null)} style={{ width: "100%", padding: "13px", borderRadius: 14, fontFamily: "Syne", fontWeight: 700, fontSize: 14, background: "var(--warm)", color: "var(--muted)", border: "none", cursor: "pointer" }}>Cancel</button>
          </div>
        </div>,
        document.body
      )}

      {/* ── Add Review comment modal ── */}
      {commentModal && createPortal(
        <div style={{ position: "fixed", top: 0, left: 0, right: 0, bottom: 0, background: "rgba(0,0,0,0.5)", backdropFilter: "blur(8px)", WebkitBackdropFilter: "blur(8px)", display: "flex", alignItems: "flex-end", zIndex: 1000, paddingBottom: "calc(80px + env(safe-area-inset-bottom, 0px))" }}>
          <div className="aslide" style={{ background: "var(--card)", borderRadius: 24, padding: "24px 20px", margin: "0 16px", width: "100%" }}>
            <div style={{ fontFamily: "Syne", fontWeight: 700, fontSize: 17, color: "var(--ink)", marginBottom: 4 }}>Write a Review</div>
            <div style={{ fontSize: 13, color: "var(--muted)", marginBottom: 16 }}>Share your experience with {commentModal.ratedName}</div>
            <textarea
              value={commentText}
              onChange={e => setCommentText(e.target.value)}
              placeholder="How was your experience?"
              maxLength={300}
              rows={4}
              style={{ width: "100%", padding: "12px 14px", borderRadius: 12, border: "1.5px solid var(--border)", background: "var(--paper)", fontSize: 14, color: "var(--ink)", resize: "none", fontFamily: "DM Sans", outline: "none" }}
            />
            <div style={{ fontSize: 11, color: "var(--muted)", textAlign: "right", marginTop: 4 }}>{commentText.length}/300</div>
            <div style={{ display: "flex", gap: 10, marginTop: 12 }}>
              <button onClick={() => { setCommentModal(null); setCommentText(""); }} style={{ flex: 1, padding: "14px", borderRadius: 14, fontFamily: "Syne", fontWeight: 700, fontSize: 14, background: "var(--warm)", color: "var(--muted)", border: "none", cursor: "pointer" }}>Cancel</button>
              <button onClick={saveComment} disabled={!commentText.trim()} style={{ flex: 1, padding: "14px", borderRadius: 14, fontFamily: "Syne", fontWeight: 700, fontSize: 14, background: commentText.trim() ? "var(--accent)" : "var(--warm)", color: commentText.trim() ? "#fff" : "var(--muted)", border: "none", cursor: "pointer", transition: "all .15s" }}>Save Review</button>
            </div>
          </div>
        </div>,
        document.body
      )}

      {/* ── Confirm modal ── */}
      {confirmAction && createPortal(
        <div style={{ position: "fixed", top: 0, left: 0, right: 0, bottom: 0, background: "rgba(0,0,0,0.5)", display: "flex", alignItems: "flex-end", zIndex: 1000, paddingBottom: "calc(80px + env(safe-area-inset-bottom, 0px))" }}>
          <div className="aslide" style={{ background: "var(--card)", borderRadius: 24, padding: "24px 20px", margin: "0 16px", width: "100%" }}>
            <div style={{ fontFamily: "Syne", fontWeight: 700, fontSize: 17, color: "var(--ink)", marginBottom: 8 }}>Are you sure?</div>
            <div style={{ fontSize: 14, color: "var(--muted)", marginBottom: 24, whiteSpace: "pre-line" }}>{confirmAction.message}</div>
            <div style={{ display: "flex", gap: 10 }}>
              <button onClick={() => { if (!confirmBusy) setConfirmAction(null); }} disabled={confirmBusy} style={{ flex: 1, padding: "14px", borderRadius: 14, fontFamily: "Syne", fontWeight: 700, fontSize: 14, background: "var(--warm)", color: "var(--ink)", border: "none", cursor: confirmBusy ? "not-allowed" : "pointer", opacity: confirmBusy ? 0.5 : 1 }}>{confirmAction.type === "trip" ? "Keep my trip" : "Keep my seat"}</button>
              <button disabled={confirmBusy} onClick={async () => {
                if (confirmBusy) return;
                setConfirmBusy(true);
                try {
                  await confirmAction.onConfirm();
                  setConfirmAction(null);
                } catch (e) {
                  console.error("Confirm action failed:", e);
                  toast("Something went wrong. Please try again.", "error");
                } finally {
                  setConfirmBusy(false);
                }
              }} style={{ flex: 1, padding: "14px", borderRadius: 14, fontFamily: "Syne", fontWeight: 700, fontSize: 14, background: "rgba(192,57,43,0.08)", color: "#c0392b", border: "1px solid rgba(192,57,43,0.15)", cursor: confirmBusy ? "wait" : "pointer", display: "flex", alignItems: "center", justifyContent: "center", gap: 8 }}>
                {confirmBusy && <span style={{ width: 14, height: 14, border: "2px solid #c0392b", borderTopColor: "transparent", borderRadius: "50%", animation: "igo-spin .7s linear infinite", display: "inline-block", flexShrink: 0 }}/>}
                {confirmBusy ? "Cancelling…" : confirmAction.type === "trip" ? "Yes, cancel trip" : "Yes, cancel seat"}
              </button>
            </div>
          </div>
        </div>,
        document.body
      )}
    </div>
  );
};

export default TripsScreen;
